import { supabase } from "@/integrations/supabase/client";
import type { BlogPost } from "@/types/blog";

export interface BlogPostFormData {
  title: string;
  slug?: string;
  excerpt?: string;
  content?: string;
  featured_image?: string;
  category?: string;
  tags?: string[];
  published?: boolean;
}

function withPermissionGuard(error: any, action: string): never {
  if (error && typeof error.message === "string" && error.message.includes("row-level security")) {
    throw new Error(
      `${action}. Certifique-se de que o usuário autenticado esteja registrado como admin na tabela app_roles do Supabase.`,
    );
  }

  throw error;
}

export const blogAdminService = {
  async listPosts(filters?: {
    search?: string;
    status?: "all" | "published" | "draft";
    category?: string;
    page?: number;
    limit?: number;
  }) {
    try {
      let query = supabase
        .from("blog_posts")
        .select("*", { count: "exact" })
        .order("created_at", { ascending: false });

      if (filters?.search) {
        query = query.ilike("title", `%${filters.search}%`);
      }

      if (filters?.status === "published") {
        query = query.eq("published", true);
      } else if (filters?.status === "draft") {
        query = query.eq("published", false);
      }

      if (filters?.category) {
        query = query.eq("category", filters.category);
      }

      const page = filters?.page || 1;
      const limit = filters?.limit || 20;
      const from = (page - 1) * limit;
      const to = from + limit - 1;

      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        posts: data as BlogPost[],
        total: count || 0,
        page,
        limit,
        totalPages: Math.ceil((count || 0) / limit),
      };
    } catch (error: any) {
      console.error("Error listing posts:", error);
      throw new Error(error.message || "Erro ao listar posts");
    }
  },

  async getPost(id: string) {
    try {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data as BlogPost;
    } catch (error: any) {
      console.error("Error getting post:", error);
      throw new Error(error.message || "Erro ao buscar post");
    }
  },

  async createPost(postData: BlogPostFormData) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error("Usuário não autenticado");

      const { data, error } = await supabase
        .from("blog_posts")
        .insert({
          ...postData,
          author_id: user.id,
          author: user.email,
        })
        .select()
        .single();

      if (error) {
        withPermissionGuard(error, "Seu usuário não tem permissão para criar posts");
      }
      return data as BlogPost;
    } catch (error: any) {
      console.error("Error creating post:", error);
      throw new Error(error.message || "Erro ao criar post");
    }
  },

  async updatePost(id: string, postData: BlogPostFormData) {
    try {
      const { data, error } = await supabase
        .from("blog_posts")
        .update(postData)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        withPermissionGuard(error, "Seu usuário não tem permissão para atualizar posts");
      }
      return data as BlogPost;
    } catch (error: any) {
      console.error("Error updating post:", error);
      throw new Error(error.message || "Erro ao atualizar post");
    }
  },

  async deletePost(id: string) {
    try {
      const { error } = await supabase
        .from("blog_posts")
        .delete()
        .eq("id", id);

      if (error) {
        withPermissionGuard(error, "Seu usuário não tem permissão para excluir posts");
      }
    } catch (error: any) {
      console.error("Error deleting post:", error);
      throw new Error(error.message || "Erro ao excluir post");
    }
  },

  async publishPost(id: string, published: boolean) {
    try {
      const { data, error } = await supabase
        .from("blog_posts")
        .update({ published })
        .eq("id", id)
        .select()
        .single();

      if (error) {
        withPermissionGuard(error, "Seu usuário não tem permissão para alterar a publicação do post");
      }
      return data as BlogPost;
    } catch (error: any) {
      console.error("Error publishing/unpublishing post:", error);
      throw new Error(error.message || "Erro ao publicar/despublicar post");
    }
  },

  async uploadCover(file: File): Promise<string> {
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("blog-covers")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (uploadError) {
        withPermissionGuard(uploadError, "Seu usuário não tem permissão para enviar imagens");
      }

      const { data } = supabase.storage
        .from("blog-covers")
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error: any) {
      console.error("Error uploading cover:", error);
      throw new Error(error.message || "Erro ao fazer upload da capa");
    }
  },

  async deleteCover(url: string) {
    try {
      const path = url.split("/blog-covers/").pop();
      if (!path) return;

      const { error } = await supabase.storage
        .from("blog-covers")
        .remove([path]);

      if (error) {
        withPermissionGuard(error, "Seu usuário não tem permissão para remover imagens");
      }
    } catch (error: any) {
      console.error("Error deleting cover:", error);
      // Não lançar erro para não bloquear outras operações
    }
  },
};
